/**
 * Media Lease Hook
 * Manages signed URLs for protected media with automatic renewal
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { LEASE_RENEW_THRESHOLD_MS } from '@gallery/shared';

interface LeaseInfo {
  artworkId: string;
  signedUrl: string;
  expiresAt: number;
  type: 'image' | 'video';
}

interface LeaseResponse {
  success: boolean;
  data?: {
    artworkId: string;
    signedUrl: string;
    expiresAt: string;
    type: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface UseMediaLeaseOptions {
  roomCode?: string;
  onLeaseError?: (artworkId: string, error: string) => void;
}

export function useMediaLease({ roomCode, onLeaseError }: UseMediaLeaseOptions = {}) {
  const [leases, setLeases] = useState<Map<string, LeaseInfo>>(new Map());
  const renewalTimersRef = useRef<Map<string, number>>(new Map());

  /**
   * Request a new lease for an artwork
   */
  const requestLease = useCallback(
    async (artworkId: string): Promise<LeaseInfo | null> => {
      try {
        const response = await fetch('/api/v1/media/lease', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            artworkId,
            roomCode,
          }),
        });

        const data: LeaseResponse = await response.json();

        if (!data.success || !data.data) {
          onLeaseError?.(artworkId, data.error?.message || 'Failed to get lease');
          return null;
        }

        const leaseInfo: LeaseInfo = {
          artworkId: data.data.artworkId,
          signedUrl: data.data.signedUrl,
          expiresAt: new Date(data.data.expiresAt).getTime(),
          type: data.data.type as 'image' | 'video',
        };

        setLeases((prev) => new Map(prev).set(artworkId, leaseInfo));

        // Schedule renewal before expiry
        scheduleRenewal(artworkId, leaseInfo.expiresAt);

        return leaseInfo;
      } catch (err) {
        console.error('Lease request failed:', err);
        onLeaseError?.(artworkId, 'Network error');
        return null;
      }
    },
    [roomCode, onLeaseError]
  );

  /**
   * Request leases for multiple artworks
   */
  const requestBatchLeases = useCallback(
    async (artworkIds: string[]): Promise<Map<string, LeaseInfo>> => {
      try {
        const response = await fetch('/api/v1/media/batch-lease', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            artworkIds,
            roomCode,
          }),
        });

        const data = await response.json();

        if (!data.success || !data.data?.leases) {
          return new Map();
        }

        const newLeases = new Map<string, LeaseInfo>();

        for (const lease of data.data.leases) {
          const leaseInfo: LeaseInfo = {
            artworkId: lease.artworkId,
            signedUrl: lease.signedUrl,
            expiresAt: new Date(lease.expiresAt).getTime(),
            type: lease.type,
          };

          newLeases.set(lease.artworkId, leaseInfo);
          scheduleRenewal(lease.artworkId, leaseInfo.expiresAt);
        }

        setLeases((prev) => {
          const updated = new Map(prev);
          for (const [id, lease] of newLeases) {
            updated.set(id, lease);
          }
          return updated;
        });

        return newLeases;
      } catch (err) {
        console.error('Batch lease request failed:', err);
        return new Map();
      }
    },
    [roomCode]
  );

  /**
   * Schedule lease renewal before expiry
   */
  const scheduleRenewal = useCallback(
    (artworkId: string, expiresAt: number) => {
      // Clear any existing timer
      const existingTimer = renewalTimersRef.current.get(artworkId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Calculate renewal time (1 minute before expiry)
      const renewAt = expiresAt - LEASE_RENEW_THRESHOLD_MS;
      const delay = renewAt - Date.now();

      if (delay <= 0) {
        // Already expired or about to, renew immediately
        requestLease(artworkId);
        return;
      }

      const timer = window.setTimeout(() => {
        requestLease(artworkId);
      }, delay);

      renewalTimersRef.current.set(artworkId, timer);
    },
    [requestLease]
  );

  /**
   * Get the current signed URL for an artwork
   */
  const getSignedUrl = useCallback(
    (artworkId: string): string | null => {
      const lease = leases.get(artworkId);
      if (!lease) return null;

      // Check if expired
      if (Date.now() >= lease.expiresAt) {
        // Trigger renewal
        requestLease(artworkId);
        return null;
      }

      return lease.signedUrl;
    },
    [leases, requestLease]
  );

  /**
   * Check if a lease is valid
   */
  const hasValidLease = useCallback(
    (artworkId: string): boolean => {
      const lease = leases.get(artworkId);
      return !!lease && Date.now() < lease.expiresAt;
    },
    [leases]
  );

  /**
   * Clear all leases and timers
   */
  const clearLeases = useCallback(() => {
    for (const timer of renewalTimersRef.current.values()) {
      clearTimeout(timer);
    }
    renewalTimersRef.current.clear();
    setLeases(new Map());
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of renewalTimersRef.current.values()) {
        clearTimeout(timer);
      }
    };
  }, []);

  return {
    leases,
    requestLease,
    requestBatchLeases,
    getSignedUrl,
    hasValidLease,
    clearLeases,
  };
}
